import { useCallback, useEffect, useRef } from 'react';

import dayjs from 'dayjs';

import { MDText } from 'i18n-react';

import { Button, TickCircleIcon } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';
import { INFOBOX_VARIANT } from '@motorway/mw-highway-code/enums';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { POST_SALE_SNOW_PLOW_EVENTS } from 'Utilities/analytics/analyticsPostSale';

import { useMarkAsCollected } from '../hooks/useMarkAsCollected';
import { useVehicleCollectionStatusSub } from '../hooks/useVehicleCollectionStatusSub';
import { useVehicleCollectionSub } from '../hooks/useVehicleCollectionSub';
import { COLLECTION_DETAILS_STATUSES_MAP, vehicleCollectionStore } from '../VehicleCollectionSubjects';

import Texts from './MarkAsCollectedActionCenter.text.json';

import styles from './MarkAsCollectedActionCenter.scss';

const LocalT = new MDText(Texts);
export const MarkAsCollectedActionCenter = () => {
  const { collectionDetails, collectionPreferences } = useVehicleCollectionSub();
  const collectionStatus = useVehicleCollectionStatusSub();
  const { data, loading, markAsCollected, statusCode } = useMarkAsCollected();
  const isLoading = collectionPreferences?.loading || collectionDetails?.loading || loading;
  const cleanupRef = useRef<() => void>(undefined);

  const saleId = collectionDetails?.data?.saleId;

  const onMarkAsUncollectedHandler = useCallback(async () => {
    if (saleId) {
      POST_SALE_SNOW_PLOW_EVENTS.COLLECTION_CONFIRMED_UNDO();
      await markAsCollected(saleId, false);

      cleanupRef?.current?.();

      vehicleCollectionStore.updateCollectedAt('');
    }
  }, [markAsCollected, saleId]);

  useEffect(() => {
    if (statusCode?.current >= 200 && statusCode?.current < 300) {
      cleanupRef.current = toast({
        content: LocalT.translate('confirmedCollectionToast.message', { action: <Hyperlink label={LocalT.translate('confirmedCollectionToast.action')} onClick={onMarkAsUncollectedHandler} variant="secondary" /> }),
        duration: 20000,
        id: 'mark-as-collected-success-toast',
        variant: INFOBOX_VARIANT.INFO,
      });
      POST_SALE_SNOW_PLOW_EVENTS.COLLECTION_CONFIRMED();

      vehicleCollectionStore.updateCollectedAt(data?.collection?.collectedAt ?? dayjs().toISOString());
    }
  }, [onMarkAsUncollectedHandler, statusCode, data]);

  const onMarkAsCollectedHandler = async () => {
    if (saleId) {
      await markAsCollected(saleId);
    }
  };

  const markAsCollectedIcon = () => <TickCircleIcon filled={false} />;

  return (
    <div className={styles.markAsCollectedActionCenter}>
      <span className={styles.icon}>
        <Button
          fullWidth
          reverse
          disabled={collectionStatus === COLLECTION_DETAILS_STATUSES_MAP.COLLECTION_UNKNOWN}
          icon={markAsCollectedIcon}
          label={LocalT.translate('button.label')}
          loading={isLoading}
          onClick={onMarkAsCollectedHandler}
          type="button"
        />
      </span>
    </div>
  );
};
